<template>
  <v-dialog v-model="dialog" persistent max-width="800">
    <!-- Snackbar -->
    <SnackbarMessage ref="snackbar" />
    <!-- Open button -->
    <template v-slot:activator="{ on: dialog }">
      <v-list-item v-on="dialog" @click.stop="dialog = true">
        <v-icon class="secondary--text">mdi-receipt-text-outline</v-icon>
        <v-list-item-title class="secondary--text">
          Facturar
        </v-list-item-title>
      </v-list-item>
    </template>
    <!-- Dialog -->
    <v-card>
      <!-- Título -->
      <v-card-title>
        Facturar venta
      </v-card-title>
      <!-- Contenido -->
      <v-card-text>
        <!-- Datos del cliente -->
        <p class="mb-2">Datos del cliente</p>
        <v-row>
          <v-col class="pt-0 pb-0" cols="12">
            <v-text-field
              v-model="customer.legal_name"
              label="Nombre Fiscal o Razón Social del cliente. sin el régimen societario"
              filled
              rounded
              dense
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 pb-0" cols="6">
            <v-text-field
              v-model="customer.tax_id"
              label="RFC"
              filled
              rounded
              dense
              required
            ></v-text-field>
          </v-col>
          <v-col class="pt-0 pb-0" cols="6">
            <v-select
              v-model="customer.tax_system"
              :items="getTaxSystems()"
              label="Régimen fiscal"
              filled
              rounded
              dense
              required
              item-text="value"
              item-value="key"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 pb-0" cols="12" md="6">
            <v-text-field
              v-model="customer.email"
              label="Correo electrónico"
              filled
              rounded
              dense
              required
            ></v-text-field>
          </v-col>
          <v-col class="pt-0 pb-0" cols="12" md="6">
            <v-text-field
              v-model="customer.phone"
              label="Número telefónico"
              filled
              rounded
              dense
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 pb-0">
            <v-text-field
              v-model="customer.address.zip"
              label="Código postal"
              filled
              rounded
              dense
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 pb-0">
            <v-select
              v-model="payment_form"
              :items="paymentFormOptions"
              label="Forma de pago"
              filled
              rounded
              dense
              required
              item-text="name"
              item-value="value"
            />
          </v-col>
          <v-col class="pt-0 pb-0">
            <v-select
              v-model="use"
              :items="usoCFDIOptions"
              label="Uso CFDI"
              filled
              rounded
              dense
              required
              item-text="name"
              item-value="value"
            />
          </v-col>
        </v-row>
        <!-- Datos de los productos -->
        <v-flex xs12>
          <!-- Headers -->
          <v-row>
            <v-col cols="3" class="text-left pb-0">
              <p class="font-weight-medium pa-0 mb-0 text--disabled">
                Descripción
              </p>
            </v-col>
            <v-col cols="4" class="text-left pb-0">
              <p class="font-weight-medium pa-0 mb-0 text--disabled">
                Clave de producto/servicio
              </p>
            </v-col>
            <v-col cols="2" class="text-left pb-0">
              <p class="font-weight-medium pa-0 mb-0 text--disabled">
                Precio
              </p>
            </v-col>
            <v-col cols="3" class="text-left pb-0">
              <p class="font-weight-medium pa-0 mb-0 text--disabled">
                Impuestos incluidos
              </p>
            </v-col>
          </v-row>
          <!-- Items -->
          <v-row v-for="(item, index) in items" :key="index">
            <v-col cols="3" class="text-left">
              <p class="pa-0 mb-0">{{ item.product.description }}</p>
            </v-col>
            <v-col cols="4" class="text-left">
              <v-text-field
                v-model="item.product.product_key"
                filled
                rounded
                dense
                required
                class="m-0 mt-0 pt-0"
                hide-details
              />
            </v-col>
            <v-col cols="2" class="text-left">
              <p class="pa-0 mb-0">{{ item.product.price }}</p>
            </v-col>
            <v-col cols="3" class="text-left">
              <v-checkbox
                v-model="item.product.tax_included"
                color="secondary"
                hide-details
                class="m-0 mt-0 pt-0"
              />
            </v-col>
          </v-row>
        </v-flex>
      </v-card-text>
      <!-- Acciones -->
      <v-card-actions>
        <v-spacer></v-spacer>
        <!-- Botón que cancela la operación -->
        <v-btn class="red--text" text @click="dialog = false">
          Cancelar
        </v-btn>
        <!-- Botón para facturar  -->
        <v-btn
          :color="mainColor"
          text
          @click="billSale"
          :loading="isSavingBill"
          :disabled="isButtonDisabled"
        >
          Facturar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Importar mixins
import taxSystems from "@/mixins/taxSystems.js";

// Importar servicios
import BillingServices from "@/services/BillingService.js";

// Importar componentes
import SnackbarMessage from "@/components/SnackbarMessage";

export default {
  mixins: [taxSystems],
  props: {
    saleData: {
      type: Object
    },
    products: {
      type: Array
    }
  },
  components: {
    SnackbarMessage
  },
  data() {
    return {
      dialog: false,
      mainColor: "#1DDF96",
      isSavingBill: false,
      paymentFormOptions: [
        {
          name: "Efectivo",
          value: "01"
        },
        {
          name: "Tarjeta de débito",
          value: "28"
        },
        {
          name: "Tarjeta de crédito",
          value: "04"
        },
        {
          name: "Transferencia electrónica de fondos",
          value: "03"
        },
        {
          name: "Cheque nominativo",
          value: "02"
        },
        {
          name: "Monedero electrónico",
          value: "05"
        },
        {
          name: "Dinero electrónico",
          value: "06"
        },
        {
          name: "Vales de despensa",
          value: "08"
        },
        {
          name: "Dación en pago",
          value: "12"
        },
        {
          name: "Pago por subrogación",
          value: "13"
        },
        {
          name: "Pago por consignación",
          value: "14"
        },
        {
          name: "Condonación",
          value: "15"
        },
        {
          name: "Compensación",
          value: "17"
        },
        {
          name: "Novación",
          value: "23"
        },
        {
          name: "Confusión",
          value: "24"
        },
        {
          name: "Remisión de deuda",
          value: "25"
        },
        {
          name: "Prescripción o caducidad",
          value: "26"
        },
        {
          name: "A satisfacción del acreedor",
          value: "27"
        },
        {
          name: "Tarjeta de servicios",
          value: "29"
        },
        {
          name: "Aplicación de anticipos",
          value: "30"
        },
        {
          name: "Intermediario pagos",
          value: "31"
        },
        {
          name: "Por definir",
          value: "99"
        }
      ],
      usoCFDIOptions: [
        {
          name: "Adquisición de mercancías",
          value: "G01"
        },
        {
          name: "Devoluciones, descuentos o bonificaciones",
          value: "G02"
        },
        {
          name: "Gastos en general",
          value: "G03"
        },
        {
          name: "Construcciones",
          value: "I01"
        },
        {
          name: "Mobiliario y equipo de oficina por inversiones",
          value: "I02"
        },
        {
          name: "Equipo de transporte",
          value: "I03"
        },
        {
          name: "Equipo de computo y accesorios",
          value: "I04"
        },
        {
          name: "Dados, troqueles, moldes, matrices y herramental",
          value: "I05"
        },
        {
          name: "Comunicaciones telefónicas",
          value: "I06"
        },
        {
          name: "Comunicaciones satelitales",
          value: "I07"
        },
        {
          name: "Otra maquinaria y equipo",
          value: "I08"
        },
        {
          name: "Honorarios médicos, dentales y gastos hospitalarios",
          value: "D01"
        },
        {
          name: "Gastos médicos por incapacidad o discapacidad",
          value: "D02"
        },
        {
          name: "Gastos funerales",
          value: "D03"
        },
        {
          name: "Donativos",
          value: "D04"
        },
        {
          name:
            "Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)",
          value: "D05"
        },
        {
          name: "Aportaciones voluntarias al SAR",
          value: "D06"
        },
        {
          name: "Primas por seguros de gastos médicos",
          value: "D07"
        },
        {
          name: "Gastos de transportación escolar obligatoria",
          value: "D08"
        },
        {
          name:
            "Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones",
          value: "D09"
        },
        {
          name: "Pagos por servicios educativos (colegiaturas)",
          value: "D10"
        },
        {
          name: "Sin efectos fiscales",
          value: "S01"
        },
        {
          name: "Pagos",
          value: "CP01"
        },
        {
          name: "Nómina",
          value: "CN01"
        }
      ],
      customer: {
        legal_name: "",
        tax_id: "",
        tax_system: "",
        address: {
          zip: ""
        },
        email: "",
        phone: ""
      },
      payment_form: "",
      use: "",
      items: this.products.map(product => {
        return {
          product: {
            description: product.name,
            product_key: null,
            price: product.pricePerUnit,
            tax_included: true
          },
          discount: product.discount || 0,
          quantity: product.quantity
        };
      })
    };
  },
  computed: {
    areProductsValid() {
      return this.items.every(item => {
        return (
          item.product.description &&
          item.product.product_key &&
          item.product.price
        );
      });
    },
    isButtonDisabled() {
      return (
        !this.customer.legal_name ||
        !this.customer.tax_id ||
        !this.customer.tax_system ||
        !this.customer.email ||
        !this.customer.phone ||
        !this.customer.address.zip ||
        !this.payment_form ||
        !this.use ||
        !this.areProductsValid ||
        this.isSavingBill
      );
    }
  },
  methods: {
    async billSale() {
      this.isSavingBill = true;

      try {
        await BillingServices.generateBill({
          saleId: this.saleData._id,
          customer: this.customer,
          payment_form: this.payment_form,
          use: this.use,
          items: this.items
        });

        // Mostrar mensaje de éxito
        this.$refs.snackbar.showSuccessMessage("Factura generada con éxito");

        // Cerrar modal
        this.isSavingBill = false;
        this.dialog = false;
      } catch (err) {
        // Mostrar error
        this.$refs.snackbar.showError(err.message);
      }
    }
  }
};
</script>
